import type { BuyerSmartImportRejectedRowDto } from "@/api/buyerSmartImport";

/**
 * The rejected-rows download.
 *
 * <p>🔴 <b>Built here, from the last dry-run, and stored nowhere.</b> It is not
 * an endpoint and it is not in the batch history: the rejected rows are raw
 * cells out of a file the platform never persists (D5), so the only place they
 * exist is the preview the operator is looking at. A server-side download would
 * mean keeping the file, which is the one thing this feature may not do.</p>
 */

/**
 * The characters that turn a spreadsheet cell into a formula.
 *
 * <p>🔴 <b>This is the whole reason the download is not a naive join.</b> A
 * rejected row is UNTRUSTED text — it came out of somebody else's file, and it
 * was rejected precisely because it was malformed. A cell reading
 * <code>=HYPERLINK(...)</code> or <code>+cmd|' /c calc'!A0</code> executes when
 * the operator opens the download in Excel or Sheets, in their session, on
 * their machine. Prefixing an apostrophe forces the cell to TEXT, which is what
 * the operator wanted to read anyway.</p>
 */
const FORMULA_LEADERS = ["=", "+", "-", "@"] as const;

/**
 * The first character a spreadsheet would actually look at.
 *
 * <p>🔴 <b>Leading whitespace and control characters are SKIPPED, because a
 * spreadsheet skips them too.</b> A cell beginning `"\u0001="` is a formula to
 * Excel and not a formula to a naive first-character test, which is exactly the
 * shape somebody reaches for when a defence like this one is in the way.</p>
 *
 * <p>Written as a scan rather than a regular expression on purpose: a pattern
 * carrying a literal control-character range trips `no-control-regex`, and
 * silencing that rule here would silence it for whatever else the file grows.
 * The test is per-character, so `\s` in it matches no control range.</p>
 */
function firstMeaningfulChar(cell: string): string {
  for (const ch of cell) {
    const code = ch.codePointAt(0) ?? 0;
    // C0 controls and space, DEL, then anything else Unicode calls whitespace.
    if (code <= 0x20 || code === 0x7f || /\s/.test(ch)) continue;
    return ch;
  }
  return "";
}

/** True when a spreadsheet would read this cell as a formula. */
export function isFormulaCell(cell: string): boolean {
  return (FORMULA_LEADERS as readonly string[]).includes(firstMeaningfulChar(cell));
}

/**
 * Force a cell to text if a spreadsheet would otherwise evaluate it.
 *
 * <p>The apostrophe goes in front of the ORIGINAL cell, not the trimmed one —
 * the operator is reading these to work out why a row was turned away, and
 * silently re-trimming their data would be a second edit on top of the one that
 * defused it.</p>
 */
export function defuseCell(cell: string): string {
  return isFormulaCell(cell) ? `'${cell}` : cell;
}

/** RFC 4180 quoting, applied AFTER defusing so the apostrophe is inside. */
function quote(cell: string): string {
  const defused = defuseCell(cell);
  return /[",\r\n]/.test(defused) ? `"${defused.replace(/"/g, '""')}"` : defused;
}

/**
 * The widest row decides the column count, so a short row is padded rather
 * than left ragged — a ragged CSV opens with cells under the wrong headings,
 * which is exactly the confusion this download exists to remove.
 */
function widestRow(rows: readonly BuyerSmartImportRejectedRowDto[]): number {
  return rows.reduce((max, r) => Math.max(max, r.cells.length), 0);
}

/** The fixed columns in front of the raw cells. */
export const REJECTED_ROWS_HEADERS = ["Tab", "Row", "Reason"] as const;

/**
 * The CSV text for a set of rejected rows.
 *
 * <p>Always emits a header line, even for an empty set: a file with no rows and
 * no headings is indistinguishable from a download that failed.</p>
 */
export function buildRejectedRowsCsv(
  rows: readonly BuyerSmartImportRejectedRowDto[],
): string {
  const width = widestRow(rows);
  const header = [
    ...REJECTED_ROWS_HEADERS,
    ...Array.from({ length: width }, (_, i) => `Cell ${i + 1}`),
  ];
  const lines = [header.map(quote).join(",")];
  for (const row of rows) {
    const padded = Array.from({ length: width }, (_, i) => row.cells[i] ?? "");
    lines.push(
      [row.tabName, String(row.rowNumber), row.reason, ...padded].map(quote).join(","),
    );
  }
  return `${lines.join("\r\n")}\r\n`;
}

/**
 * `rejected-rows-<file>.csv`, with anything a filesystem dislikes removed.
 *
 * <p>Separators are trimmed off both ends before the fallback is considered,
 * because the substitution above can leave a stem that is ALL separator. A
 * source name of `"///"` collapses to `"-"`, which is truthy and therefore
 * sails past a bare `|| "import"` — producing `rejected-rows--.csv`, exactly
 * the nameless download the fallback exists to prevent. Trimming first means
 * the fallback sees an empty stem and fires.</p>
 */
export function rejectedRowsFileName(sourceName: string): string {
  const stem = sourceName
    .replace(/\.[^.]+$/, "")
    .replace(/[^\w.-]+/g, "-")
    .replace(/^[-.]+|[-.]+$/g, "");
  return `rejected-rows-${stem || "import"}.csv`;
}
